import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { DateRange } from 'react-day-picker';
import Spinner from 'react-spinkit';
import { Icon } from '@iconify/react';
import { RootState } from '../../ApplicationState';
import { getCookie, getLocalForage, setLocalForage } from '../../aaa';
import {
  getNewDistrictDataBySchool,
  getDistrictDataByType,
  getLegacyDistrictPlatformScript,
  getLegacyDistrictPeekavilleScript,
  getUpdatedDistrictUnifiedScript,
  getStartOfSchoolYear,
  getTopFiveSchools,
  getMixpanelDatabaseByDate,
} from '../reportUtilities';
import { getDistricts, getSchools, queryMixpanelByGroup, queryMixpanelByGroupV2 } from '../../BackendInterface';
import DistrictChart from './DistrictChart';
import DistrictSelector from '../AdminReportComponents/DistrictSelector/DistrictSelector';
import DateRangePicker from '../../components/DateRangePicker';
import DistrictDataTable from './DistrictDataTable';
import CustomDropdown from '../AdminReportComponents/CustomDropdown/CustomDropdown';
import HowToUseModal from './HowToUseModal';
import { isUserHasKey, logDebugLevel } from '../../GlobalFunctions';
import styles from './DistrictReport.module.scss';
import libraryStyles from '../../SCSS/Library.module.scss';
import {
  Districts,
  ReportingMixpanelEvent,
  DistrictDataBySchool,
  DistrictDataByType,
} from '@peekapak/types/ReportDataExchangeTypes';
import { SchoolModel } from '@peekapak/types/DataProtocolTypes';

const tableOverrideSuffix = '';

const today = new Date();
const startOfSchoolYear = getStartOfSchoolYear();

const initialDateRange: DateRange = { from: startOfSchoolYear, to: today };

const DistrictReport = () => {
  const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);
  const [isDisplayDayPicker, setIsDisplayDayPicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [districtList, setDistrictList] = useState<Districts>([]);
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [isDistrictListLoaded, setDistrictListLoaded] = useState(false);

  const [schoolList, setSchoolList] = useState<SchoolModel[]>([]);
  const [isSchoolListLoaded, setSchoolListLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [dataByType, setDataByType] = useState<DistrictDataByType>();
  const [dataBySchool, setDataBySchool] = useState<DistrictDataBySchool>();
  const [topFiveSchools, setTopFiveSchools] = useState<string[]>([]);

  const [legacyPlatformData, setLegacyPlatformData] = useState<ReportingMixpanelEvent[]>([]);
  const [legacyPeekavilleData, setLegacyPeekavilleData] = useState<ReportingMixpanelEvent[]>([]);
  const [newMixpanelData, setNewMixpanelData] = useState<ReportingMixpanelEvent[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const [mixpanelError, setMixpanelError] = useState<string>('');

  const [eventToChart, setEventToChart] = useState<string>('educator_logins');

  const eventNames = [
    {
      name: 'Number of Staff',
      id: 'number_of_educators',
    },
    {
      name: 'Staff Logins',
      id: 'educator_logins',
    },
    {
      name: 'Student Logins',
      id: 'student_logins',
    },
    {
      name: 'Story Opens',
      id: 'story_opens',
    },
    {
      name: 'Lessons Accessed',
      id: 'lessons_accessed',
    },
    {
      name: 'Time on Platform (hrs)',
      id: 'time_on_platform',
    },
  ];

  const userProfile = useSelector((state: RootState) => {
    return state.user.userProfile;
  });

  async function onSelectDistrict(district: string) {
    if (isDistrictListLoaded && districtList.length > 0) {
      setSelectedDistrict(district);
      const storageKey = 'selectedDistrict';
      await setLocalForage(storageKey, district);
      setSchoolListLoaded(false);
    } else {
      console.warn(`%cDistrict List is Missing`, 'background: black; color:yellow');
    }
  }

  //////////////////////////////////////////////////////////////////
  //
  // initialize the list of districts
  //
  //////////////////////////////////////////////////////////////////
  useEffect(() => {
    let mounted = true;

    if (!isDistrictListLoaded) {
      getListOfDistricts();
    } else {
      restoreSavedSelectedDistrict();
    }

    return function cleanup() {
      mounted = false;
    };

    async function getListOfDistricts() {
      const storageKey = 'districtsList';
      const map = await getLocalForage(storageKey);

      if (map && mounted) {
        setDistrictList(map);
        setDistrictListLoaded(true);
      }

      const mapFromDb = await getDistricts();
      await setLocalForage(storageKey, mapFromDb, 1440);

      if (mounted) {
        setDistrictList(mapFromDb);
        setDistrictListLoaded(true);
      }
    }

    async function restoreSavedSelectedDistrict() {
      const storageKey = 'selectedDistrict';
      const selectedDistrict = await getLocalForage(storageKey);

      if (selectedDistrict) {
        setSelectedDistrict(selectedDistrict);
      }
    }
  }, [districtList, isDistrictListLoaded, userProfile, selectedDistrict]);

  //////////////////////////////////////////////////////////////////
  //
  // initialize the list of schools in the selected district
  //
  //////////////////////////////////////////////////////////////////
  useEffect(() => {
    let mounted = true;

    if (!isSchoolListLoaded) {
      getListOfSchoolsInDistrict();
    }

    return function cleanup() {
      mounted = false;
    };

    // load list of schools
    async function getListOfSchoolsInDistrict() {
      const selectedDistrictStorageKey = 'selectedDistrict';
      const districtForQuery =
        selectedDistrict || (await getLocalForage(selectedDistrictStorageKey)) || userProfile?.district || '';

      // if there is no district for getting the list of schools, then there's
      // no point to continuing (check?)
      if (!districtForQuery) {
        return;
      }

      const schoolListStorageKey = `${districtForQuery}-schoolsList`;
      const list = (await getLocalForage(schoolListStorageKey))?.filter((s: SchoolModel) => !s.isArchived);

      if (list?.length > 0 && mounted) {
        setSchoolList(list);
        setSchoolListLoaded(true);
        return;
      }

      // if the list is not in local storage, then get it from the database
      const listFromDb = (await getSchools(districtForQuery, tableOverrideSuffix))?.filter((s) => !s.isArchived);
      await setLocalForage(schoolListStorageKey, listFromDb, 43200);

      if (mounted) {
        setSchoolList(listFromDb);
        setSchoolListLoaded(true);
      }
    }
  }, [isDistrictListLoaded, userProfile, selectedDistrict, isSchoolListLoaded]);

  //////////////////////////////////////////////////////////////////
  //
  // AFTER the district list is loaded, set the selected district
  //
  //////////////////////////////////////////////////////////////////
  useEffect(() => {
    run();

    async function run() {
      if (isDistrictListLoaded && districtList.length > 0) {
        const storageKey = 'selectedDistrict';
        const selectedDistrict: string = (await getLocalForage(storageKey)) as unknown as string;

        if (selectedDistrict) {
          setSelectedDistrict(selectedDistrict);
        } else {
          const userDistrict = districtList.some((district) => district.id === userProfile?.district)
            ? (userProfile?.district ?? '')
            : '';
          return setSelectedDistrict(userDistrict);
        }
      }
    }
  }, [userProfile, isDistrictListLoaded, districtList]);

  const handleLoadData = () => {
    retrieveMixpanelData();
    setMixpanelError('');
  };

  const handleBeforeStart = () => {
    setMixpanelError(
      `You've requested data from before the current period. Please click the refresh button to update.`,
    );
  };

  useEffect(() => {
    retrieveMixpanelData();
  }, [selectedDistrict, dateRange]);

  const retrieveMixpanelData = async () => {
    if (!selectedDistrict) return;
    const fromDate = dateRange !== undefined ? dateRange['from'] : initialDateRange['from'];
    const toDate = dateRange !== undefined ? dateRange['to'] : initialDateRange['to'];

    getMixpanelDataBasedOnDateRange(fromDate as Date, toDate as Date, selectedDistrict);
  };

  const getMixpanelDataBasedOnDateRange = async (startDate: Date, endDate: Date, selectedDistrict: string) => {
    logDebugLevel(`District Report retrieving data for ${selectedDistrict} from ${startDate} to ${endDate}`);
    setIsLoading(true);
    setIsDataLoaded(false);
    try {
      const DBtoQuery = getMixpanelDatabaseByDate(startDate, endDate);
      if (DBtoQuery === 'legacyDB') {
        const platformResult = await queryMixpanelByGroup(
          getLegacyDistrictPlatformScript(
            startDate.toISOString().substring(0, 10),
            endDate.toISOString().substring(0, 10),
            selectedDistrict,
          ),
          'peekapak',
          tableOverrideSuffix,
        );
        setLegacyPlatformData(platformResult);
        const peekavilleResult = await queryMixpanelByGroup(
          getLegacyDistrictPeekavilleScript(
            startDate.toISOString().substring(0, 10),
            endDate.toISOString().substring(0, 10),
            selectedDistrict,
          ),
          'mypeekaville',
          tableOverrideSuffix,
        );
        setLegacyPeekavilleData(peekavilleResult);
        setNewMixpanelData([]);
      } else if (DBtoQuery === 'newDB') {
        const result = await queryMixpanelByGroupV2(
          getUpdatedDistrictUnifiedScript(
            startDate.toISOString().substring(0, 10),
            endDate.toISOString().substring(0, 10),
            selectedDistrict,
          ),
          'unified',
          tableOverrideSuffix,
        );
        setNewMixpanelData(result);
        setLegacyPeekavilleData([]);
        setLegacyPlatformData([]);
      } else if (DBtoQuery === 'mixed') {
        const result = await queryMixpanelByGroupV2(
          getUpdatedDistrictUnifiedScript(
            startDate.toISOString().substring(0, 10),
            endDate.toISOString().substring(0, 10),
            selectedDistrict,
          ),
          'unified',
          tableOverrideSuffix,
        );
        setNewMixpanelData(result);
        const platformResult = await queryMixpanelByGroup(
          getLegacyDistrictPlatformScript(
            startDate.toISOString().substring(0, 10),
            endDate.toISOString().substring(0, 10),
            selectedDistrict,
          ),
          'peekapak',
          tableOverrideSuffix,
        );
        setLegacyPlatformData(platformResult);
        const peekavilleResult = await queryMixpanelByGroup(
          getLegacyDistrictPeekavilleScript(
            startDate.toISOString().substring(0, 10),
            endDate.toISOString().substring(0, 10),
            selectedDistrict,
          ),
          'mypeekaville',
          tableOverrideSuffix,
        );
        setLegacyPeekavilleData(peekavilleResult);
      } else
        setMixpanelError(
          'Uh oh! Could not locate correct database for given date range. Please try again later or contact Peekapak support.',
        );
    } catch (error) {
      setMixpanelError('Uh oh! Something went wrong. Please try again later or contact Peekapak support.');
      console.error(error);
    } finally {
      setIsDataLoaded(true);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isDataLoaded) {
      getDataByType();
    }
  }, [isDataLoaded, newMixpanelData, legacyPeekavilleData, legacyPlatformData, dateRange]);

  const getDataByType = useCallback(async () => {
    setIsLoading(true);
    try {
      const districtDataByType = getDistrictDataByType(legacyPeekavilleData, legacyPlatformData, newMixpanelData);
      setDataByType(districtDataByType);
      const dataBySchool = getNewDistrictDataBySchool(districtDataByType, schoolList);
      setDataBySchool(dataBySchool);
      const topFiveSchools = getTopFiveSchools(dataBySchool, eventToChart);
      setTopFiveSchools(topFiveSchools);
    } finally {
      setIsLoading(false);
    }
  }, [newMixpanelData, legacyPeekavilleData, legacyPlatformData, dateRange]);

  const toggleShowModal = () => {
    setShowModal(true);
  };

  useEffect(() => {
    const cookie = getCookie('howToUseDistrictReporting');
    if (cookie === 'neverShowAgain') {
      setShowModal(false);
    } else {
      setShowModal(true);
    }
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.titleAndSelectors}>
        <h1>District Reporting</h1>
        {isDistrictListLoaded && (
          <DistrictSelector
            isPeekapakAdmin={!!isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')}
            districtsList={districtList}
            onSelectDistrict={onSelectDistrict}
            initialValue={selectedDistrict}
            currentlySelectedDistrict={selectedDistrict}
            disableChangeLocation={false}
          />
        )}
        <div className={styles.eventType}>
          <CustomDropdown
            list={eventNames}
            onSelectSubject={setEventToChart}
            initialValue={eventToChart}
            currentlySelectedItem={eventToChart}
          />
        </div>
      </div>
      <div className={styles.dateAndSubmit}>
        <DateRangePicker
          dateRange={dateRange}
          setDateRange={setDateRange}
          startDate={startOfSchoolYear}
          excludeAfter={today}
          beforeFirstInputText='Start Date:'
          beforeSecondInputText='End Date:'
          isDisplayCalendar={isDisplayDayPicker}
          setIsDisplayCalendar={setIsDisplayDayPicker}
          handleBeforeStart={handleBeforeStart}
        />
        <Icon className={styles.reloadIcon} icon='zondicons:reload' onClick={handleLoadData} />
        {mixpanelError && <p className={styles.mixpanelError}>{mixpanelError}</p>}
        <div className={styles.doNotShowAgain}>
          <button onClick={toggleShowModal} className={libraryStyles.textLink}>
            How do I use this tool?
          </button>
        </div>
      </div>
      {isDataLoaded && dataBySchool && topFiveSchools && !isLoading ? (
        <>
          <DistrictChart
            data={dataByType}
            eventToChart={eventToChart}
            startDate={dateRange.from}
            endDate={dateRange.to}
            schoolList={schoolList}
            topFiveSchools={topFiveSchools}
          />
          <DistrictDataTable data={dataBySchool} topFiveSchools={topFiveSchools} />
        </>
      ) : (
        <div className={styles.loadingContainer}>
          <Spinner />
        </div>
      )}
      <HowToUseModal show={showModal} handleHideModal={() => setShowModal(false)} />
    </div>
  );
};

// note the lock decorator now expressed as an HOC call at the end of the source
// // code file

// const LockableDistrictReport = lockWithPreviewUnlessHasAny(
//   'KEY_ADMINISTRATOR_REPORTS',
//   'KEY_PEEKAPAK_MANAGEMENT',
//   'KEY_WIP',
//   'KEY_MASTER_KEY'
// )(DistrictReport);

export default DistrictReport;
